/**
 * Tag normalization and formatting utilities for forum tags
 */

// Standard forum tags mapping - kebab-case to Title Case
export const STANDARD_TAGS = {
  'career-advice': 'Career Advice',
  'job-search': 'Job Search',
  'interview-tips': 'Interview Tips',
  'networking': 'Networking',
  'salary-negotiation': 'Salary Negotiation',
  'remote-work': 'Remote Work',
  'startup-life': 'Startup Life',
  'tech-industry': 'Tech Industry'
};

// Preset tag values that should be filtered from display
export const PRESET_TAG_VALUES = [
  'career-advice',
  'job-search', 
  'interview-tips',
  'networking',
  'salary-negotiation',
  'remote-work',
  'startup-life',
  'tech-industry'
];

/**
 * Convert display format (Title Case) to storage format (kebab-case)
 * @param {string} tag - Tag in display format (e.g., "Career Advice")
 * @returns {string} - Tag in storage format (e.g., "career-advice")
 */
export const normalizeTagForStorage = (tag) => {
  if (!tag || typeof tag !== 'string') {
    return '';
  }
  
  return tag
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '') // Remove special characters except hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Convert storage format (kebab-case) to display format (Title Case)
 * @param {string} tag - Tag in storage format (e.g., "career-advice")
 * @returns {string} - Tag in display format (e.g., "Career Advice")
 */
export const formatTagForDisplay = (tag) => {
  if (!tag || typeof tag !== 'string') {
    return '';
  }
  
  // Check if it's a standard tag first
  if (STANDARD_TAGS[tag]) {
    return STANDARD_TAGS[tag];
  }
  
  // Fallback formatting for non-standard tags
  return tag
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Get all standard tag values (kebab-case format)
 * @returns {string[]} - Array of standard tag values
 */
export const getStandardTagValues = () => {
  return Object.keys(STANDARD_TAGS);
};

/**
 * Get all standard tag labels (Title Case format)
 * @returns {string[]} - Array of standard tag labels
 */
export const getStandardTagLabels = () => {
  return Object.values(STANDARD_TAGS);
};

/**
 * Check if a tag is a standard forum tag
 * @param {string} tag - Tag to check (in any format)
 * @returns {boolean} - True if tag is standard
 */
export const isStandardTag = (tag) => {
  if (!tag || typeof tag !== 'string') {
    return false;
  }
  
  const normalizedTag = normalizeTagForStorage(tag);
  return STANDARD_TAGS.hasOwnProperty(normalizedTag);
};

/**
 * Normalize an array of tags for storage
 * @param {string[]} tags - Array of tags in any format
 * @returns {string[]} - Array of normalized tags (kebab-case)
 */
export const normalizeTagsForStorage = (tags) => {
  if (!Array.isArray(tags)) {
    return [];
  }
  
  return tags
    .map(tag => normalizeTagForStorage(tag))
    .filter(tag => tag.length > 0) // Remove empty tags
    .filter((tag, index, array) => array.indexOf(tag) === index); // Remove duplicates
};

/**
 * Handle legacy tag formats and migrate them to standard format
 * @param {string[]} tags - Array of tags that might be in legacy format
 * @returns {string[]} - Array of normalized tags
 */
export const migrateLegacyTags = (tags) => {
  if (!Array.isArray(tags)) {
    return [];
  }

  // Legacy tag mappings for backward compatibility
  const legacyTagMappings = {
    'professional-development': 'career-advice',
    'job-hunting': 'job-search',
    'work-from-home': 'remote-work',
    'startup': 'startup-life',
    'technology': 'tech-industry',
    'interviews': 'interview-tips',
    'salary': 'salary-negotiation',
    'network': 'networking'
  };

  return tags
    .map(tag => {
      const normalizedTag = normalizeTagForStorage(tag);
      // Check if it's a legacy tag that needs migration
      return legacyTagMappings[normalizedTag] || normalizedTag;
    })
    .filter(tag => tag.length > 0)
    .filter((tag, index, array) => array.indexOf(tag) === index);
};

/**
 * Check if a tag is a preset tag that should be filtered from display
 * @param {string} tag - Tag to check (in any format)
 * @returns {boolean} - True if tag is a preset tag
 */
export const isPresetTag = (tag) => {
  if (!tag || typeof tag !== 'string') {
    return false;
  }
  
  const normalizedTag = normalizeTagForStorage(tag);
  return PRESET_TAG_VALUES.includes(normalizedTag);
};

/**
 * Filter out preset tags from an array of tags
 * @param {string[]} tags - Array of tags in any format
 * @returns {string[]} - Array of tags with preset tags removed
 */
export const filterPresetTags = (tags) => {
  if (!Array.isArray(tags)) {
    return [];
  }
  
  try {
    return tags.filter(tag => !isPresetTag(tag));
  } catch (error) {
    console.error('Error filtering preset tags:', error);
    // Return original tags if filtering fails to avoid breaking UI
    return tags;
  }
};

/**
 * Format an array of tags for display
 * @param {string[]} tags - Array of tags in storage format
 * @returns {string[]} - Array of formatted tags (Title Case)
 */
export const formatTagsForDisplay = (tags) => {
  if (!Array.isArray(tags)) {
    return [];
  }
  
  return tags.map(tag => formatTagForDisplay(tag));
};